import modelDefaults from '@/mixins/geometryUpdates/modelDefaults';
import swatchesUpdates from '@/mixins/geometryUpdates/swatchesUpdates';
import merchandisingRules from '@/mixins/geometryUpdates/merchandisingRules';
import productVisibility from '@/mixins/geometryUpdates/productVisibility';
import ugcFeatures from '@/mixins/geometryUpdates/ugcFeatures';

export default {
  mixins: [
    modelDefaults,
    swatchesUpdates,
    merchandisingRules,
    productVisibility,
    ugcFeatures
  ],
  computed: {
    pdm() {
      return this.$store.state.utils.pdm;
    },
    activeComponent() {
      return this.$store.state.legacyNavigation.activeComponent;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
  },
  methods: {
    isGeometryDefaultState(geometryUpdate) {
      return this.pdm.merchandising_rules?.swatches?.[geometryUpdate]?.default_state;
    },
    /**
     * @function validateGeometryUpdate
     * @param {string} - current component's name
     * @param {string} - current geometry update to apply
     * @param {boolea} - set to true if the change is coming from the applyRecipe flow
     *
     * Validate if a geometry should change based geometries already applied and merchadisign rules
     */
    validateGeometryUpdate({ geometryUpdate, componentName, applyingRecipe }) {
      const isGeoAlreadyLoaded = this.isGeoAlreadyLoaded({ componentName, geometryUpdate });
      const isGeoInRecipe = this.isGeoAppliedInRecipe({ geometryUpdate, componentName });
      const shouldIgnoreMerchRulesRecipe = this.$store.state.utils.isRandomizeApplied &&
        this.isGeometryDefaultState(geometryUpdate);
      const validateMerchRulesRecipe = !this.validateMerchandisingRulesRecipe({ componentName, geometryUpdate });
      const validateMerchandisingRulesPDM = this.validateMerchandisingRulesPDM({ geometryUpdate, componentName, applyingRecipe });

      return ((!isGeoAlreadyLoaded && !isGeoInRecipe) || applyingRecipe)
        && validateMerchandisingRulesPDM
        && (validateMerchRulesRecipe || shouldIgnoreMerchRulesRecipe && !validateMerchRulesRecipe);
    },
    /**
     * @function isGeoAlreadyLoaded
     * @param {string} - current component's name
     * @param {string} - current geometry update to apply
     *
     * Checks if the Swatch's Geo update is already applied in current swatch
     */
    isGeoAlreadyLoaded({ componentName, geometryUpdate }) {
      const recipeComponents = this.recipe_v2.components;
      const swatchId = recipeComponents[componentName].swatch_id;
      const swatchObj = this.$store.getters["swatches/returnSwatchObj"]({ swatchId });
      const { tempData } = this.$store.state.ugc;
      const activeUGCFeature = tempData.features[tempData.activeUGCIndex].ugcFeature;

      const isValidGeometryByFeature = this.isValidFeatureGeoUpdate({ componentName, feature: recipeComponents[componentName].subgroup });
      if ((activeUGCFeature && geometryUpdate.includes(activeUGCFeature)) || isValidGeometryByFeature) {
        // Get Updates from Feature Component Subgroup
        const geometryUpdates = this.isGeoUpdateFromFeature({ componentName, geometrySwatch: swatchObj && swatchObj.geometry_updates, geometryUpdate });
        return geometryUpdates && geometryUpdates.includes(geometryUpdate);
      }
      return swatchObj && swatchObj.geometry_updates && swatchObj.geometry_updates.includes(geometryUpdate);
    },
    /**
     * @function isGeoAlreadyLoaded
     * @param {string} - current geometry update to apply
     *
     * Checks if Swatch's Geo Update is already applied in Recipe
     * If the swatch geo is a default state, it ignores the result and allows the flow to continue.
     */
    isGeoAppliedInRecipe({ geometryUpdate, componentName }) {
      let isGeoAppliedInRecipe;
      let validateInRecipe;
      const recipeComponents = this.recipe_v2.components;
      const components = Object.keys(recipeComponents);

      if (this.isGeometryDefaultState(geometryUpdate) || this.validateMerchandisingByPDMComponent({ rule: geometryUpdate, componentName, validActiveComponent: !this.$store.state.utils.isFinishMyDesignApplied })) {
        isGeoAppliedInRecipe = false;
      } else {
        validateInRecipe = components.filter(component => {
          let swatchObj = this.$store.getters['swatches/returnSwatchObj']({ swatchId: recipeComponents[component].swatch_id });
          return swatchObj && swatchObj.geometry_updates && swatchObj.geometry_updates.includes(geometryUpdate);
        });
        isGeoAppliedInRecipe = validateInRecipe && validateInRecipe.length > 0;
      }

      return isGeoAppliedInRecipe;
    },
    /**
     *
     * @param {*} param0
     * @returns
     */
    swapGeometries({ geometryUpdate, updateType }) {
      return new Promise(async (resolve) => {
        let geosToRemove;
        const geoUpdates = this.pdm.geometry_updates && this.pdm.geometry_updates[updateType];
        if (geoUpdates && geoUpdates[geometryUpdate] && geoUpdates[geometryUpdate].remove_geo) {
          geosToRemove = this.setGeosToRemove({ geosToRemove: geoUpdates[geometryUpdate].remove_geo });
        }
        if (geoUpdates && geoUpdates[geometryUpdate] && geoUpdates[geometryUpdate].add_geo) {
          try {
            const geoObjs = await this.loadNewGeometry({ newGeos: geoUpdates[geometryUpdate].add_geo })
            this.removeGeos({ geosToRemove, componentsToRemove: geoUpdates[geometryUpdate].remove_geo} );
            geoObjs.forEach(geoObj => this.$model3D.modelObj.add(geoObj));
            this.$model3D.sceneHelpers.parseModelComponents(this.$model3D.modelObj, false);
            resolve();
          } catch (e) {console.error('loadNewGeometry: ', e)};
        } else {
          resolve();
        }
      });
    },
    removeGeos({ geosToRemove, componentsToRemove=null }) {
      if (!geosToRemove || geosToRemove.length === 0) return false;
      geosToRemove.forEach((geo,i) => {
        if (componentsToRemove && this.$model3D.sceneHelpers.getSceneObjectByName({objectName: componentsToRemove[i]})) {
          this.$model3D.sceneHelpers.disposeMaterial({
            material: this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentsToRemove[i] }).material
          });
          this.$model3D.sceneHelpers.removeSceneObjectByName(componentsToRemove[i]);
        }
        this.$model3D.modelObj.remove(geo);
        this.$model3D.sceneHelpers.disposeMeshObject({ meshObject: geo })

      });
    },
    setGeosToRemove({ geosToRemove }) {
      const geoObjects = [];
      geosToRemove.forEach(geo => {
        const selectedObject = this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: geo });
        if (selectedObject) {
          selectedObject.name = selectedObject.name + "_remove";
          geoObjects.push(selectedObject);
        }
      });
      return geoObjects;
    },
    async loadNewGeometry({ newGeos }) {
      return new Promise((resolve, reject) => {
        let promises = [];
        const size = this.$store.state.sceneSetup.modelBySize;
        const modelSKU = this.pdm.parent_model || this.pdm.sku;
        newGeos.forEach(geo => {
          const geoUrl = this.$store.getters["utils/addCacheBusting"](`${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${modelSKU}/${size}/${geo.path}`);
          promises.push(
            new Promise(resolve => {
              this.$model3D.loadObject(geoUrl).then(obj => {
                const geoObj = obj.getObjectByName(geo.component_name);
                resolve(geoObj);
              }).catch(e => console.error('loadObject: ', e));
            })
          )
        });
        resolve(Promise.all(promises).catch(reject));
      })
    }
  }
};
